import React, { useRef, useState } from "react";
import video1 from "../../assets/New_data/video1.mp4";
import video2 from "../../assets/New_data/video2.mp4";
import video3 from "../../assets/New_data/video3.mp4";

const videos = [
  {
    id: "01",
    title: "Campus Walkthrough",
    description: "A tour of the learning workstation in a live classroom.",
    fallbackDuration: "3:06",
    src: video1,
    poster: "/videos/video1-poster.jpg",
  },
  {
    id: "02",
    title: "Applications Experience",
    description: "How Applications work and How to use this platform day to day.",
    fallbackDuration: "4:38",
    src: video2,
    poster: "/videos/video2-poster.jpg",
  },
  {
    id: "03",
    title: "Adhesive electrodes on the chest",
    description: "These are connected to a monitor to track the baby’s heart rate and breathing.",
    fallbackDuration: "2:02",
    src: video3,
    poster: "/videos/video3-poster.jpg",
  },
   {
    id: "04",
    title: "Preventive Screening Of Thalassemia & Sickle Cell Desease ",
    description: "Burden of Thalassemia & Sickle Cell Desease in India : State wise analysis & epidemiological overview",
    fallbackDuration: "2:51",
    src: "https://res.cloudinary.com/mim9fwes/video/upload/v1784806905/Thalassemia_Burden-Odisha-Pitch_Video-compressed-compressed-compressed_xwdgyt.mp4",
    poster: "/videos/video3-poster.jpg",
  },
];

const formatTime = (seconds) => {
  if (!Number.isFinite(seconds)) return null;
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, "0")}`;
};

const VideoShowcase = () => {
  return (
    <section className="bg-white text-[#14161A] font-['Inter',sans-serif] border-t border-[#14161A]">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@500;600;700&family=Inter:wght@400;500;600&family=IBM+Plex+Mono:wght@400;500&display=swap');
        .font-display { font-family: 'Space Grotesk', sans-serif; }
        .font-mono-tag { font-family: 'IBM Plex Mono', monospace; }

        @keyframes vs-scan {
          0% { transform: translateY(-100%); }
          100% { transform: translateY(100%); }
        }
        .vs-scanline {
          background: linear-gradient(
            to bottom,
            transparent 0%,
            rgba(36, 81, 217, 0.12) 45%,
            rgba(36, 81, 217, 0.12) 55%,
            transparent 100%
          );
          animation: vs-scan 2.4s linear infinite;
        }
        @media (prefers-reduced-motion: reduce) {
          .vs-scanline { animation: none; display: none; }
        }
      `}</style>

      <div className="px-3 md:px-7 lg:px-20 py-16">
        <div className="flex items-center gap-3">
          <span className="font-mono-tag text-xs text-[#2451D9] border border-[#2451D9] w-6 h-6 flex items-center justify-center shrink-0">
            D
          </span>
          <h2 className="font-display text-2xl md:text-3xl">
            Field Recordings
          </h2>
        </div>

        <p className="mt-6 max-w-2xl text-[#4B5160] leading-relaxed">
          Footage from the ground — classrooms, students, and educators
          engaging with the platform in practice.
        </p>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 gap-8 items-start">
          {videos.map((v) => (
            <VideoCard key={v.id} video={v} />
          ))}
        </div>
      </div>
    </section>
  );
};

const VideoCard = ({ video }) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [elapsed, setElapsed] = useState(null);
  const [duration, setDuration] = useState(null);

  const handlePlayPause = () => {
    const el = videoRef.current;
    if (!el) return;
    if (el.paused) {
      el.play();
    } else {
      el.pause();
    }
  };

  return (
    <div className="group">
      {/* frame with corner brackets, like a viewfinder */}
      <div className="relative aspect-video bg-[#F5F7FB] overflow-hidden">
        <span className="absolute -top-px -left-px w-4 h-4 border-t-2 border-l-2 border-[#2451D9] z-10" />
        <span className="absolute -top-px -right-px w-4 h-4 border-t-2 border-r-2 border-[#2451D9] z-10" />
        <span className="absolute -bottom-px -left-px w-4 h-4 border-b-2 border-l-2 border-[#2451D9] z-10" />
        <span className="absolute -bottom-px -right-px w-4 h-4 border-b-2 border-r-2 border-[#2451D9] z-10" />

        <video
          ref={videoRef}
          src={video.src}
          // poster={video.poster}
          controls
          onPlay={() => setIsPlaying(true)}
          onPause={() => setIsPlaying(false)}
          onLoadedMetadata={(e) => setDuration(e.currentTarget.duration)}
          onTimeUpdate={(e) => setElapsed(e.currentTarget.currentTime)}
          className="absolute inset-0 w-full h-full object-cover"
        />

        {/* scan-line, only while actually playing, ignored by reduced-motion users */}
        {isPlaying && (
          <div className="absolute inset-0 pointer-events-none vs-scanline" />
        )}

        {/* large click target to start playback; native controls take over once playing */}
        {!isPlaying && (
          <button
            type="button"
            onClick={handlePlayPause}
            aria-label={`Play ${video.title}`}
            className="absolute inset-0 flex items-center justify-center focus:outline-none focus-visible:ring-2 focus-visible:ring-[#2451D9] focus-visible:ring-offset-2"
          >
            <span className="w-14 h-14 rounded-full bg-white/90 flex items-center justify-center transition-transform group-hover:scale-105">
              <svg width="18" height="18" viewBox="0 0 18 18" fill="#14161A">
                <path d="M4 2.5v13l11-6.5-11-6.5z" />
              </svg>
            </span>
          </button>
        )}

        <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-white/90 px-2 py-1">
          <span
            className={`w-1.5 h-1.5 rounded-full bg-[#2451D9] ${
              isPlaying ? "animate-pulse motion-reduce:animate-none" : ""
            }`}
          />
          <span className="font-mono-tag text-[10px] tracking-widest text-[#14161A] uppercase">
            Rec-{video.id}
          </span>
        </div>

        <div className="absolute bottom-3 right-3 bg-white/90 px-2 py-1">
          <span className="font-mono-tag text-[10px] text-[#6B7280] tabular-nums">
            {formatTime(elapsed) ?? "0:00"} / {formatTime(duration) ?? video.fallbackDuration}
          </span>
        </div>
      </div>

      <div className="mt-4">
        <div className="font-mono-tag text-[10px] tracking-widest text-[#E8A33D] mb-1">
          CLIP {video.id} · 16:9
        </div>
        <h3 className="font-display text-lg">{video.title}</h3>
        <p className="mt-1 text-sm text-[#6B7280] leading-relaxed">
          {video.description}
        </p>
      </div>
    </div>
  );
};

export default VideoShowcase;